import * as React from 'react'
import { FieldsetInput } from '../../components/FieldsetInput'
import { FieldsetSelect } from '../../components/FieldsetSelect'
import { Button } from '@/ui'
import { Modal } from './Modal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type UserFormValues = {
  login: string
  firstName: string
  lastName: string
  roleId: string
  userId?: string
  email?: string
  phone?: string
  driverId?: string
}

type FieldErrors = Partial<Record<'email' | 'firstName' | 'lastName' | 'roleId' | 'driverId', string>>

type UsersModalMode = 'create' | 'edit'

type RoleOption = { value: string; label: string; isDriver?: boolean }

type UsersModalProps = {
  isOpen: boolean
  mode: UsersModalMode
  initialValues?: UserFormValues
  rolesOptions: RoleOption[]
  driversOptions?: { value: string; label: string }[]
  pending?: boolean
  error?: string | null
  onCancel: () => void
  onSubmit: (values: UserFormValues) => void
}

export const UsersModal: React.FC<UsersModalProps> = ({
  isOpen,
  mode,
  initialValues,
  rolesOptions,
  driversOptions,
  pending,
  error,
  onCancel,
  onSubmit,
}) => {
  const { t } = useLocalize()
  const [values, setValues] = React.useState<UserFormValues>({
    login: '',
    firstName: '',
    lastName: '',
    roleId: '',
    userId: '',
    email: '',
    phone: '',
    driverId: '',
  })
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({})

  // Sync form with incoming initial values when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const defaultValues = {
        login: '',
        firstName: '',
        lastName: '',
        roleId: '',
        userId: '',
        email: '',
        phone: '',
        driverId: '',
      }
      
      if (initialValues) {
        // Sync email and login - prefer email if available, otherwise use login
        const emailValue = initialValues.email || initialValues.login || ''
        setValues({
          ...initialValues,
          email: emailValue,
          login: emailValue,
        })
      } else {
        setValues(defaultValues)
      }
      setFieldErrors({})
    }
  }, [initialValues, isOpen])

  const handleChange =
    (field: keyof UserFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      setValues((prev) => ({ ...prev, [field]: value }))
      if (field === 'firstName' || field === 'lastName') {
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    // Update both email and login fields with the same value
    setValues((prev) => ({ ...prev, email: value, login: value }))
    setFieldErrors((prev) => ({ ...prev, email: undefined }))
  }

  const handleRoleChange = (value: string) => {
    setValues((prev) => ({ ...prev, roleId: value, driverId: '' }))
    setFieldErrors((prev) => ({ ...prev, roleId: undefined, driverId: undefined }))
  }

  const isSelectedRoleDriver = React.useMemo(() => {
    if (!values.roleId) return false
    return rolesOptions.find(r => r.value === values.roleId)?.isDriver ?? false
  }, [values.roleId, rolesOptions])

  const validate = (submitValues: UserFormValues): FieldErrors => {
    const errors: FieldErrors = {}
    const email = (submitValues.email || submitValues.login || '').trim()

    if (!email) {
      errors.email = t('administrationPanel-users.validation-login-empty')
    }
    if (!submitValues.firstName.trim()) {
      errors.firstName = t('administrationPanel-users.validation-firstName-empty')
    }
    if (!submitValues.lastName.trim()) {
      errors.lastName = t('administrationPanel-users.validation-surname-empty')
    }
    if (!submitValues.roleId) {
      errors.roleId = t('administrationPanel-users.validation-role')
    }
    if (isSelectedRoleDriver && !submitValues.driverId) {
      errors.driverId = 'Wybierz kierowcę...'
    }

    return errors
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Ensure login and email are synced before submitting
    const submitValues = {
      ...values,
      login: values.email || values.login,
      email: values.email || values.login,
    }
    const errors = validate(submitValues)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setFieldErrors({})
    onSubmit(submitValues)
  }

  const title = mode === 'create' 
    ? t('administrationPanel-users.add-user')
    : t('administrationPanel-users.edit-user')

  const validationBanner =
    fieldErrors.email ||
    fieldErrors.firstName ||
    fieldErrors.lastName ||
    fieldErrors.roleId ||
    fieldErrors.driverId ||
    null

  return (
    <Modal isOpen={isOpen} title={title} onClose={onCancel}>
      <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4" noValidate>
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <FieldsetInput
            name="email"
            label={t('administrationPanel-users.email')}
            type="email"
            value={values.email ?? values.login ?? ''}
            onChange={handleEmailChange}
            required
            error={fieldErrors.email}
          />

          <FieldsetInput
            name="firstName"
            label={t('administrationPanel-users.firstName')}
            value={values.firstName}
            onChange={handleChange('firstName')}
            required
            error={fieldErrors.firstName}
          />

          <FieldsetInput
            name="lastName"
            label={t('administrationPanel-users.surname')}
            value={values.lastName}
            onChange={handleChange('lastName')}
            required
            error={fieldErrors.lastName}
          />

          <FieldsetInput
            name="phone"
            label={t('administrationPanel-users.phone')}
            type="tel"
            value={values.phone ?? ''}
            onChange={handleChange('phone')}
          />

          <FieldsetSelect
            name="roleId"
            label={t('administrationPanel-users.role')}
            value={values.roleId}
            handleChange={handleRoleChange}
            options={rolesOptions}
            placeholder={t('administrationPanel-users.choose-role')}
            required
            error={fieldErrors.roleId}
          />

          {isSelectedRoleDriver && driversOptions && (
            <FieldsetSelect
              name="driverId"
              label="Kierowca"
              value={values.driverId ?? ''}
              handleChange={(val) => {
                setValues((prev) => ({ ...prev, driverId: val }))
                setFieldErrors((prev) => ({ ...prev, driverId: undefined }))
              }}
              options={driversOptions}
              placeholder="Wybierz kierowcę..."
              required
              error={fieldErrors.driverId}
            />
          )}
        </div>

        {(validationBanner || error) && (
          <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3">
            <p className="tw-text-sm tw-text-red-700">{validationBanner || error}</p>
          </div>
        )}

        <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="tw-rounded-lg tw-px-4"
            onClick={onCancel}
            disabled={pending}
          >
            {t('answer.cancel')}
          </Button>
          <Button
            type="submit"
            size="sm"
            className="tw-rounded-lg tw-px-4"
            disabled={pending}
          >
            {mode === 'create'
              ? t('administrationPanel-users.add-user')
              : t('operation.save')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// Convenience wrappers – you can import/use these wprost

type AddUserModalProps = Omit<
  UsersModalProps,
  'mode' | 'initialValues' | 'error'
> & {
  initialValues?: UserFormValues
  error?: string | null
  driversOptions?: { value: string; label: string }[]
}

export const AddUserModal: React.FC<AddUserModalProps> = (props) => (
  <UsersModal {...props} mode="create" />
)

type EditUserModalProps = Omit<UsersModalProps, 'mode'> & {}

export const EditUserModal: React.FC<EditUserModalProps> = (props) => (
  <UsersModal {...props} mode="edit" />
)
